/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Grid,
    Alert,
    CircularProgress,
    Chip,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Slider,
    IconButton,
    Collapse,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Flight,
    Search,
    FlightTakeoff,
    FlightLand,
    Close,
    FilterList,
    ExpandLess,
    ExpandMore,
    CheckCircle,
    Recommend,
    AirlineSeatReclineNormal,
    SwapHoriz,
    Add,
    Remove,
    Person,
    Edit,
} from '@mui/icons-material';
import { Autocomplete, Popper } from '@mui/material';
// ============================================================================
// TYPES
// ============================================================================

interface FlightSearchDialogProps {
    open: boolean;
    onClose: () => void;
    tripId: string;
    origin: string;
    destination: string;
    departDate: string;
    returnDate?: string;
    passengers?: number;
    onFlightsSelected: (flights: FlightResult[], searchParams?: { origin: string; destination: string; passengers: number }) => Promise<void>;
}

interface FlightResult {
    id: string;
    airline: string;
    airlineLogo: string;
    flightNumber: string;
    departureAirport: string;
    departureCity: string;
    departureTime: string;
    arrivalAirport: string;
    arrivalCity: string;
    arrivalTime: string;
    duration: string;
    stops: number;
    stopCities?: string[];
    price: number;
    currency: string;
    cabinClass: string;
    seatsLeft?: number;
    recommended?: boolean;
    origin?: string;
    destination?: string;
    returnFlight?: {
        flightNumber: string;
        departureTime: string;
        arrivalTime: string;
        duration: string;
        stops: number;
    };
}

// ============================================================================
// STEPPER COMPONENT - Reusable +/- control for mobile-friendly number input
// ============================================================================

interface StepperInputProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    label: string;
    icon?: React.ReactNode;
}

const StepperInput: React.FC<StepperInputProps> = ({
    value,
    onChange,
    min = 1,
    max = 9,
    label,
    icon,
}) => {
    const handleDecrement = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (value > min) onChange(value - 1);
    };

    const handleIncrement = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (value < max) onChange(value + 1);
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            border: '1px solid #ddd',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            bgcolor: 'white',
            minHeight: 56,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                    size="small"
                    onClick={handleDecrement}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    disabled={value <= min}
                    sx={{ 
                        bgcolor: '#f5f5f5', 
                        width: 32, 
                        height: 32,
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        '&:hover': { bgcolor: '#e0e0e0' },
                        '&.Mui-disabled': { bgcolor: '#fafafa', cursor: 'default' },
                    }}
                >
                    <Remove fontSize="small" />
                </IconButton>
                <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 600, userSelect: 'none' }}>
                    {value}
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleIncrement}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    disabled={value >= max}
                    sx={{ 
                        bgcolor: '#f5f5f5', 
                        width: 32, 
                        height: 32,
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        '&:hover': { bgcolor: '#e0e0e0' },
                        '&.Mui-disabled': { bgcolor: '#fafafa', cursor: 'default' },
                    }}
                >
                    <Add fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
};
// ============================================================================
// AIRPORT DATA & AUTOCOMPLETE
// ============================================================================

interface AirportOption {
    code: string;
    name: string;
    city: string;
    country: string;
    label: string; // Display label: "JFK - New York John F. Kennedy"
}

// Common airports database (expandable - can be loaded from API later)
const AIRPORTS: AirportOption[] = [
    // United States - Major Hubs
    { code: 'JFK', name: 'John F. Kennedy International', city: 'New York', country: 'US', label: 'JFK - New York John F. Kennedy' },
    { code: 'LGA', name: 'LaGuardia', city: 'New York', country: 'US', label: 'LGA - New York LaGuardia' },
    { code: 'EWR', name: 'Newark Liberty International', city: 'Newark', country: 'US', label: 'EWR - Newark Liberty International' },
    { code: 'LAX', name: 'Los Angeles International', city: 'Los Angeles', country: 'US', label: 'LAX - Los Angeles International' },
    { code: 'SFO', name: 'San Francisco International', city: 'San Francisco', country: 'US', label: 'SFO - San Francisco International' },
    { code: 'ORD', name: "O'Hare International", city: 'Chicago', country: 'US', label: "ORD - Chicago O'Hare" },
    { code: 'MDW', name: 'Midway International', city: 'Chicago', country: 'US', label: 'MDW - Chicago Midway' },
    { code: 'ATL', name: 'Hartsfield-Jackson Atlanta International', city: 'Atlanta', country: 'US', label: 'ATL - Atlanta Hartsfield-Jackson' },
    { code: 'DFW', name: 'Dallas/Fort Worth International', city: 'Dallas', country: 'US', label: 'DFW - Dallas/Fort Worth' },
    { code: 'DEN', name: 'Denver International', city: 'Denver', country: 'US', label: 'DEN - Denver International' },
    { code: 'SEA', name: 'Seattle-Tacoma International', city: 'Seattle', country: 'US', label: 'SEA - Seattle-Tacoma' },
    { code: 'MIA', name: 'Miami International', city: 'Miami', country: 'US', label: 'MIA - Miami International' },
    { code: 'FLL', name: 'Fort Lauderdale-Hollywood International', city: 'Fort Lauderdale', country: 'US', label: 'FLL - Fort Lauderdale' },
    { code: 'MCO', name: 'Orlando International', city: 'Orlando', country: 'US', label: 'MCO - Orlando International' },
    { code: 'BOS', name: 'Logan International', city: 'Boston', country: 'US', label: 'BOS - Boston Logan' },
    { code: 'PHX', name: 'Phoenix Sky Harbor International', city: 'Phoenix', country: 'US', label: 'PHX - Phoenix Sky Harbor' },
    { code: 'IAH', name: 'George Bush Intercontinental', city: 'Houston', country: 'US', label: 'IAH - Houston George Bush' },
    { code: 'HOU', name: 'William P. Hobby', city: 'Houston', country: 'US', label: 'HOU - Houston Hobby' },
    { code: 'LAS', name: 'Harry Reid International', city: 'Las Vegas', country: 'US', label: 'LAS - Las Vegas Harry Reid' },
    { code: 'MSP', name: 'Minneapolis-Saint Paul International', city: 'Minneapolis', country: 'US', label: 'MSP - Minneapolis-Saint Paul' },
    { code: 'DTW', name: 'Detroit Metropolitan', city: 'Detroit', country: 'US', label: 'DTW - Detroit Metropolitan' },
    { code: 'PHL', name: 'Philadelphia International', city: 'Philadelphia', country: 'US', label: 'PHL - Philadelphia International' },
    { code: 'CLT', name: 'Charlotte Douglas International', city: 'Charlotte', country: 'US', label: 'CLT - Charlotte Douglas' },
    { code: 'SAN', name: 'San Diego International', city: 'San Diego', country: 'US', label: 'SAN - San Diego International' },
    { code: 'TPA', name: 'Tampa International', city: 'Tampa', country: 'US', label: 'TPA - Tampa International' },
    { code: 'DCA', name: 'Ronald Reagan Washington National', city: 'Washington', country: 'US', label: 'DCA - Washington Reagan National' },
    { code: 'IAD', name: 'Washington Dulles International', city: 'Washington', country: 'US', label: 'IAD - Washington Dulles' },
    { code: 'BWI', name: 'Baltimore/Washington International', city: 'Baltimore', country: 'US', label: 'BWI - Baltimore/Washington' },
    
    // International - Europe
    { code: 'LHR', name: 'Heathrow', city: 'London', country: 'UK', label: 'LHR - London Heathrow' },
    { code: 'LGW', name: 'Gatwick', city: 'London', country: 'UK', label: 'LGW - London Gatwick' },
    { code: 'STN', name: 'Stansted', city: 'London', country: 'UK', label: 'STN - London Stansted' },
    { code: 'CDG', name: 'Charles de Gaulle', city: 'Paris', country: 'FR', label: 'CDG - Paris Charles de Gaulle' },
    { code: 'ORY', name: 'Orly', city: 'Paris', country: 'FR', label: 'ORY - Paris Orly' },
    { code: 'AMS', name: 'Schiphol', city: 'Amsterdam', country: 'NL', label: 'AMS - Amsterdam Schiphol' },
    { code: 'FRA', name: 'Frankfurt Airport', city: 'Frankfurt', country: 'DE', label: 'FRA - Frankfurt Airport' },
    { code: 'MUC', name: 'Munich Airport', city: 'Munich', country: 'DE', label: 'MUC - Munich Airport' },
    { code: 'BCN', name: 'El Prat', city: 'Barcelona', country: 'ES', label: 'BCN - Barcelona El Prat' },
    { code: 'MAD', name: 'Adolfo Suárez Madrid-Barajas', city: 'Madrid', country: 'ES', label: 'MAD - Madrid Barajas' },
    { code: 'FCO', name: 'Leonardo da Vinci-Fiumicino', city: 'Rome', country: 'IT', label: 'FCO - Rome Fiumicino' },
    { code: 'MXP', name: 'Malpensa', city: 'Milan', country: 'IT', label: 'MXP - Milan Malpensa' },
    { code: 'ZRH', name: 'Zurich Airport', city: 'Zurich', country: 'CH', label: 'ZRH - Zurich Airport' },
    { code: 'VIE', name: 'Vienna International', city: 'Vienna', country: 'AT', label: 'VIE - Vienna International' },
    { code: 'DUB', name: 'Dublin Airport', city: 'Dublin', country: 'IE', label: 'DUB - Dublin Airport' },
    { code: 'LIS', name: 'Humberto Delgado', city: 'Lisbon', country: 'PT', label: 'LIS - Lisbon' },
    
    // International - Asia & Pacific
    { code: 'NRT', name: 'Narita International', city: 'Tokyo', country: 'JP', label: 'NRT - Tokyo Narita' },
    { code: 'HND', name: 'Haneda', city: 'Tokyo', country: 'JP', label: 'HND - Tokyo Haneda' },
    { code: 'ICN', name: 'Incheon International', city: 'Seoul', country: 'KR', label: 'ICN - Seoul Incheon' },
    { code: 'HKG', name: 'Hong Kong International', city: 'Hong Kong', country: 'HK', label: 'HKG - Hong Kong International' },
    { code: 'SIN', name: 'Changi', city: 'Singapore', country: 'SG', label: 'SIN - Singapore Changi' },
    { code: 'BKK', name: 'Suvarnabhumi', city: 'Bangkok', country: 'TH', label: 'BKK - Bangkok Suvarnabhumi' },
    { code: 'SYD', name: 'Kingsford Smith', city: 'Sydney', country: 'AU', label: 'SYD - Sydney Kingsford Smith' },
    { code: 'MEL', name: 'Melbourne Airport', city: 'Melbourne', country: 'AU', label: 'MEL - Melbourne Airport' },
    { code: 'PEK', name: 'Beijing Capital International', city: 'Beijing', country: 'CN', label: 'PEK - Beijing Capital' },
    { code: 'PVG', name: 'Pudong International', city: 'Shanghai', country: 'CN', label: 'PVG - Shanghai Pudong' },
    { code: 'DEL', name: 'Indira Gandhi International', city: 'Delhi', country: 'IN', label: 'DEL - Delhi Indira Gandhi' },
    { code: 'BOM', name: 'Chhatrapati Shivaji Maharaj', city: 'Mumbai', country: 'IN', label: 'BOM - Mumbai' },
    
    // International - Americas
    { code: 'YYZ', name: 'Toronto Pearson International', city: 'Toronto', country: 'CA', label: 'YYZ - Toronto Pearson' },
    { code: 'YVR', name: 'Vancouver International', city: 'Vancouver', country: 'CA', label: 'YVR - Vancouver International' },
    { code: 'YUL', name: 'Montréal-Trudeau International', city: 'Montreal', country: 'CA', label: 'YUL - Montreal Trudeau' },
    { code: 'MEX', name: 'Benito Juárez International', city: 'Mexico City', country: 'MX', label: 'MEX - Mexico City' },
    { code: 'CUN', name: 'Cancún International', city: 'Cancún', country: 'MX', label: 'CUN - Cancún International' },
    { code: 'GRU', name: 'São Paulo-Guarulhos', city: 'São Paulo', country: 'BR', label: 'GRU - São Paulo Guarulhos' },
    { code: 'EZE', name: 'Ministro Pistarini', city: 'Buenos Aires', country: 'AR', label: 'EZE - Buenos Aires Ezeiza' },
    { code: 'BOG', name: 'El Dorado International', city: 'Bogotá', country: 'CO', label: 'BOG - Bogotá El Dorado' },
    { code: 'LIM', name: 'Jorge Chávez International', city: 'Lima', country: 'PE', label: 'LIM - Lima Jorge Chávez' },
    { code: 'SCL', name: 'Arturo Merino Benítez', city: 'Santiago', country: 'CL', label: 'SCL - Santiago' },
    
    // Caribbean
    { code: 'SJU', name: 'Luis Muñoz Marín International', city: 'San Juan', country: 'PR', label: 'SJU - San Juan Puerto Rico' },
    { code: 'MBJ', name: 'Sangster International', city: 'Montego Bay', country: 'JM', label: 'MBJ - Montego Bay Jamaica' },
    { code: 'NAS', name: 'Lynden Pindling International', city: 'Nassau', country: 'BS', label: 'NAS - Nassau Bahamas' },
    { code: 'PUJ', name: 'Punta Cana International', city: 'Punta Cana', country: 'DO', label: 'PUJ - Punta Cana' },
    
    // Middle East & Africa
    { code: 'DXB', name: 'Dubai International', city: 'Dubai', country: 'AE', label: 'DXB - Dubai International' },
    { code: 'AUH', name: 'Abu Dhabi International', city: 'Abu Dhabi', country: 'AE', label: 'AUH - Abu Dhabi International' },
    { code: 'DOH', name: 'Hamad International', city: 'Doha', country: 'QA', label: 'DOH - Doha Hamad' },
    { code: 'TLV', name: 'Ben Gurion', city: 'Tel Aviv', country: 'IL', label: 'TLV - Tel Aviv Ben Gurion' },
    { code: 'JNB', name: 'O.R. Tambo International', city: 'Johannesburg', country: 'ZA', label: 'JNB - Johannesburg O.R. Tambo' },
    { code: 'CAI', name: 'Cairo International', city: 'Cairo', country: 'EG', label: 'CAI - Cairo International' },
];

// Neighborhood/area to airport mapping for smart suggestions
const AREA_TO_AIRPORTS: Record<string, string[]> = {
    'manhattan': ['JFK', 'LGA', 'EWR'],
    'new york': ['JFK', 'LGA', 'EWR'],
    'nyc': ['JFK', 'LGA', 'EWR'],
    'brooklyn': ['JFK', 'LGA', 'EWR'],
    'queens': ['JFK', 'LGA'],
    'south beach': ['MIA', 'FLL'],
    'miami beach': ['MIA', 'FLL'],
    'downtown miami': ['MIA'],
    'hollywood': ['LAX', 'BUR'],
    'beverly hills': ['LAX', 'BUR'],
    'santa monica': ['LAX'],
    'silicon valley': ['SFO', 'SJC'],
    'palo alto': ['SFO', 'SJC'],
    'downtown chicago': ['ORD', 'MDW'],
    'loop': ['ORD', 'MDW'],
    'waikiki': ['HNL'],
    'strip': ['LAS'],
    'vegas': ['LAS'],
    'times square': ['JFK', 'LGA', 'EWR'],
    'central london': ['LHR', 'LGW', 'STN', 'LCY'],
    'west end': ['LHR', 'LGW'],
    'city of london': ['LCY', 'LHR'],
};

// Search/filter airports based on query
const filterAirports = (query: string): AirportOption[] => {
    if (!query || query.length < 2) return [];
    
    const normalizedQuery = query.toLowerCase().trim();
    
    // Check if query matches a known area/neighborhood
    const areaMatch = Object.entries(AREA_TO_AIRPORTS).find(([area]) => 
        normalizedQuery.includes(area) || area.includes(normalizedQuery)
    );
    
    if (areaMatch) {
        const [, airportCodes] = areaMatch;
        return AIRPORTS.filter(a => airportCodes.includes(a.code));
    }
    
    // Standard search: match code, city, or airport name
    return AIRPORTS.filter(airport => {
        const searchFields = [
            airport.code.toLowerCase(),
            airport.city.toLowerCase(),
            airport.name.toLowerCase(),
            airport.label.toLowerCase(),
        ];
        return searchFields.some(field => field.includes(normalizedQuery));
    }).slice(0, 8); // Limit to 8 results
};

// Custom Popper to ensure dropdown appears correctly
const AutocompletePopper = (props: any) => (
    <Popper {...props} placement="bottom-start" style={{ zIndex: 1500 }} />
);
// ============================================================================
// MOCK DATA GENERATOR
// ============================================================================

const generateMockFlights = (origin: string, destination: string, departDate: string, returnDate?: string): FlightResult[] => {
    const originCode = origin.substring(0, 3).toUpperCase();
    const destCode = destination.substring(0, 3).toUpperCase();

    const flights: FlightResult[] = [
        {
            id: 'flight-1',
            airline: 'United Airlines',
            airlineLogo: '✈️',
            flightNumber: 'UA 1234',
            departureAirport: originCode,
            departureCity: origin,
            departureTime: '08:30',
            arrivalAirport: destCode,
            arrivalCity: destination,
            arrivalTime: '14:45',
            duration: '6h 15m',
            stops: 0,
            price: 342,
            currency: 'USD',
            cabinClass: 'Economy',
            seatsLeft: 4,
            recommended: true,
            origin: origin,
            destination: destination,
            returnFlight: returnDate ? {
                flightNumber: 'UA 5678',
                departureTime: '16:00',
                arrivalTime: '22:30',
                duration: '6h 30m',
                stops: 0,
            } : undefined,
        },
        {
            id: 'flight-2',
            airline: 'American Airlines',
            airlineLogo: '🛫',
            flightNumber: 'AA 456',
            departureAirport: originCode,
            departureCity: origin,
            departureTime: '06:15',
            arrivalAirport: destCode,
            arrivalCity: destination,
            arrivalTime: '11:30',
            duration: '5h 15m',
            stops: 0,
            price: 389,
            currency: 'USD',
            cabinClass: 'Economy',
            seatsLeft: 7,
            origin: origin,
            destination: destination,
            returnFlight: returnDate ? {
                flightNumber: 'AA 789',
                departureTime: '18:30',
                arrivalTime: '00:15',
                duration: '5h 45m',
                stops: 0,
            } : undefined,
        },
        {
            id: 'flight-3',
            airline: 'Delta Air Lines',
            airlineLogo: '🛩️',
            flightNumber: 'DL 789',
            departureAirport: originCode,
            departureCity: origin,
            departureTime: '10:45',
            arrivalAirport: destCode,
            arrivalCity: destination,
            arrivalTime: '15:30',
            duration: '4h 45m',
            stops: 0,
            price: 425,
            currency: 'USD',
            cabinClass: 'Economy',
            seatsLeft: 12,
            origin: origin,
            destination: destination,
            returnFlight: returnDate ? {
                flightNumber: 'DL 012',
                departureTime: '20:00',
                arrivalTime: '01:15',
                duration: '5h 15m',
                stops: 0,
            } : undefined,
        },
        {
            id: 'flight-4',
            airline: 'JetBlue Airways',
            airlineLogo: '💙',
            flightNumber: 'B6 234',
            departureAirport: originCode,
            departureCity: origin,
            departureTime: '14:00',
            arrivalAirport: destCode,
            arrivalCity: destination,
            arrivalTime: '21:45',
            duration: '7h 45m',
            stops: 1,
            stopCities: ['Dallas (DFW)'],
            price: 275,
            currency: 'USD',
            cabinClass: 'Economy',
            seatsLeft: 15,
            origin: origin,
            destination: destination,
            returnFlight: returnDate ? {
                flightNumber: 'B6 567',
                departureTime: '09:30',
                arrivalTime: '17:00',
                duration: '7h 30m',
                stops: 1,
            } : undefined,
        },
        {
            id: 'flight-5',
            airline: 'Southwest Airlines',
            airlineLogo: '❤️',
            flightNumber: 'WN 890',
            departureAirport: originCode,
            departureCity: origin,
            departureTime: '07:00',
            arrivalAirport: destCode,
            arrivalCity: destination,
            arrivalTime: '16:30',
            duration: '9h 30m',
            stops: 2,
            stopCities: ['Chicago (ORD)', 'Denver (DEN)'],
            price: 198,
            currency: 'USD',
            cabinClass: 'Economy',
            seatsLeft: 22,
            origin: origin,
            destination: destination,
            returnFlight: returnDate ? {
                flightNumber: 'WN 123',
                departureTime: '06:00',
                arrivalTime: '15:15',
                duration: '9h 15m',
                stops: 2,
            } : undefined,
        },
        {
            id: 'flight-6',
            airline: 'United Airlines',
            airlineLogo: '✈️',
            flightNumber: 'UA 9999',
            departureAirport: originCode,
            departureCity: origin,
            departureTime: '09:00',
            arrivalAirport: destCode,
            arrivalCity: destination,
            arrivalTime: '14:00',
            duration: '5h 00m',
            stops: 0,
            price: 892,
            currency: 'USD',
            cabinClass: 'Business',
            seatsLeft: 3,
            origin: origin,
            destination: destination,
            returnFlight: returnDate ? {
                flightNumber: 'UA 8888',
                departureTime: '17:00',
                arrivalTime: '22:30',
                duration: '5h 30m',
                stops: 0,
            } : undefined,
        },
    ];

    return flights;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FlightSearchDialog: React.FC<FlightSearchDialogProps> = ({
    open,
    onClose,
    tripId,
    origin,
    destination,
    departDate,
    returnDate,
    passengers = 1,
    onFlightsSelected,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Search form state
    const [tripType, setTripType] = useState<'roundtrip' | 'oneway'>(returnDate ? 'roundtrip' : 'oneway');
    const [searchOrigin, setSearchOrigin] = useState(origin || '');
    const [searchDestination, setSearchDestination] = useState(destination || '');
    const [searchDepartDate, setSearchDepartDate] = useState(departDate ? departDate.split('T')[0] : '');
    const [searchReturnDate, setSearchReturnDate] = useState(returnDate ? returnDate.split('T')[0] : '');
    const [searchPassengers, setSearchPassengers] = useState(passengers);
    const [cabinClass, setCabinClass] = useState<'economy' | 'premium' | 'business' | 'first'>('economy');

    // UI state
    const [searchFormExpanded, setSearchFormExpanded] = useState(true);

    // Results state
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<FlightResult[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    // Selection state
    const [selectedFlights, setSelectedFlights] = useState<string[]>([]);
    const [maxSelections] = useState(5);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Filter state
    const [showFilters, setShowFilters] = useState(false);
    const [priceRange, setPriceRange] = useState<number[]>([0, 1000]);
    const [maxStops, setMaxStops] = useState<number>(2);
    const [directOnly, setDirectOnly] = useState(false);

    // Get today's date for min attribute
    const today = new Date().toISOString().split('T')[0];

    // Update search fields when props change
    useEffect(() => {
        if (open) {
            setSearchOrigin(origin || '');
            setSearchDestination(destination || '');
            setSearchDepartDate(departDate ? departDate.split('T')[0] : '');
            setSearchReturnDate(returnDate ? returnDate.split('T')[0] : '');
            setTripType(returnDate ? 'roundtrip' : 'oneway');
            setSearchPassengers(passengers);
            setSelectedFlights([]);
            setResults([]);
            setHasSearched(false);
            setError('');
            setSearchFormExpanded(true);
        }
    }, [open, origin, destination, departDate, returnDate, passengers]);

    // Auto-update return date min when depart date changes
    useEffect(() => {
        if (searchReturnDate && searchDepartDate && searchReturnDate < searchDepartDate) {
            setSearchReturnDate(searchDepartDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDepartDate]);

    const handleSwapCities = () => {
        const temp = searchOrigin;
        setSearchOrigin(searchDestination);
        setSearchDestination(temp);
    };

    const handleSearch = async () => {
        // Validation
        if (!searchOrigin.trim() || !searchDestination.trim()) {
            setError('Please enter origin and destination');
            return;
        }
        if (!searchDepartDate) {
            setError('Please select a departure date');
            return;
        }
        if (searchDepartDate < today) {
            setError('Departure date cannot be in the past');
            return;
        }
        if (tripType === 'roundtrip') {
            if (!searchReturnDate) {
                setError('Please select a return date');
                return;
            }
            if (searchReturnDate < searchDepartDate) {
                setError('Return date must be on or after departure date');
                return;
            }
        }

        setSearching(true);
        setError('');

        try {
            await new Promise(resolve => setTimeout(resolve, 1500));

            const mockResults = generateMockFlights(
                searchOrigin,
                searchDestination,
                searchDepartDate,
                tripType === 'roundtrip' ? searchReturnDate : undefined
            );

            setResults(mockResults);
            setHasSearched(true);
            
            // Auto-collapse search form on mobile after successful search
            if (isMobile) {
                setSearchFormExpanded(false);
            }

            // Auto-select recommended flight
            const recommended = mockResults.find(f => f.recommended);
            if (recommended) {
                setSelectedFlights([recommended.id]);
            }
        } catch (err) {
            setError('Search failed. Please try again.');
        } finally {
            setSearching(false);
        }
    };

    const toggleFlightSelection = (flightId: string) => {
        setSelectedFlights(prev => {
            if (prev.includes(flightId)) {
                return prev.filter(id => id !== flightId);
            } else if (prev.length < maxSelections) {
                return [...prev, flightId];
            } else {
                setError(`You can select up to ${maxSelections} flights for comparison`);
                return prev;
            }
        });
    };

    const handleSaveSelections = async () => {
        if (selectedFlights.length === 0) {
            setError('Please select at least one flight');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const selected = results.filter(f => selectedFlights.includes(f.id));
            await onFlightsSelected(selected, {
                origin: searchOrigin,
                destination: searchDestination,
                passengers: searchPassengers,
            });
            onClose();
        } catch (err) {
            setError('Failed to save selections. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Filter results
    const filteredResults = results.filter(flight => {
        if (flight.price < priceRange[0] || flight.price > priceRange[1]) return false;
        if (flight.stops > maxStops) return false;
        if (directOnly && flight.stops > 0) return false;
        if (cabinClass !== 'economy' && flight.cabinClass.toLowerCase() !== cabinClass) return false;
        return true;
    });

    // Search summary for collapsed state
    const searchSummary = hasSearched ? (
        `${searchOrigin} → ${searchDestination} · ${searchDepartDate}${tripType === 'roundtrip' ? ` - ${searchReturnDate}` : ''} · ${searchPassengers} pax`
    ) : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ 
                sx: { 
                    maxHeight: isMobile ? '100%' : '90vh',
                    m: isMobile ? 0 : 2,
                    borderRadius: isMobile ? 0 : 2,
                } 
            }}
        >
            {/* ============================================================ */}
            {/* HEADER */}
            {/* ============================================================ */}
            <DialogTitle sx={{
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #eee',
                py: 1.5,
                px: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Flight sx={{ color: '#2196F3' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Search Flights
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {/* ============================================================ */}
                {/* SEARCH FORM - Collapsible after search */}
                {/* ============================================================ */}
                <Box sx={{ bgcolor: '#FAFAFA', borderBottom: '1px solid #eee' }}>
                    {/* Collapsed summary bar (shown when form is collapsed) */}
                    {hasSearched && !searchFormExpanded && (
                        <Box
                            onClick={() => setSearchFormExpanded(true)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1.5,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: '#f0f0f0' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Flight sx={{ fontSize: 18, color: '#2196F3' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {searchSummary}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                startIcon={<Edit sx={{ fontSize: 16 }} />}
                                sx={{ textTransform: 'none', minWidth: 'auto' }}
                            >
                                Edit
                            </Button>
                        </Box>
                    )}

                    {/* Expanded search form */}
                    <Collapse in={searchFormExpanded}>
                        <Box sx={{ p: 2 }}>
                            {/* Trip Type Toggle */}
                            <ToggleButtonGroup
                                value={tripType}
                                exclusive
                                onChange={(_, value) => value && setTripType(value)}
                                size="small"
                                sx={{ mb: 2 }}
                            >
                                <ToggleButton value="roundtrip" sx={{ textTransform: 'none', px: 2 }}>
                                    Round Trip
                                </ToggleButton>
                                <ToggleButton value="oneway" sx={{ textTransform: 'none', px: 2 }}>
                                    One Way
                                </ToggleButton>
                            </ToggleButtonGroup>

                            {/* MOBILE LAYOUT */}
                            {isMobile ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {/* Origin - Smart Autocomplete */}
                                    <Autocomplete
                                        freeSolo
                                        options={filterAirports(searchOrigin)}
                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                        inputValue={searchOrigin}
                                        onInputChange={(_, newValue) => setSearchOrigin(newValue)}
                                        onChange={(_, newValue) => {
                                            if (newValue && typeof newValue !== 'string') {
                                                setSearchOrigin(newValue.code + ' - ' + newValue.city);
                                            }
                                        }}
                                        PopperComponent={AutocompletePopper}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{ py: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                    <Box sx={{ 
                                                        bgcolor: '#E3F2FD', 
                                                        color: '#1976D2', 
                                                        px: 1, 
                                                        py: 0.5, 
                                                        borderRadius: 1,
                                                        fontWeight: 700,
                                                        fontSize: '0.85rem',
                                                        minWidth: 45,
                                                        textAlign: 'center',
                                                    }}>
                                                        {option.code}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {option.city}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {option.name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                label="From"
                                                placeholder="City or airport code"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <FlightTakeoff sx={{ mr: 1, color: '#999', fontSize: 20 }} />
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            />
                                        )}
                                    />

                                    {/* Swap button */}
                                    <Box sx={{ display: 'flex', justifyContent: 'center', my: -0.5 }}>
                                        <IconButton 
                                            onClick={handleSwapCities} 
                                            size="small"
                                            sx={{ 
                                                bgcolor: 'white', 
                                                border: '1px solid #ddd',
                                                transform: 'rotate(90deg)',
                                            }}
                                        >
                                            <SwapHoriz fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    {/* Destination - Smart Autocomplete */}
                                    <Autocomplete
                                        freeSolo
                                        options={filterAirports(searchDestination)}
                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                        inputValue={searchDestination}
                                        onInputChange={(_, newValue) => setSearchDestination(newValue)}
                                        onChange={(_, newValue) => {
                                            if (newValue && typeof newValue !== 'string') {
                                                setSearchDestination(newValue.code + ' - ' + newValue.city);
                                            }
                                        }}
                                        PopperComponent={AutocompletePopper}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props} sx={{ py: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                    <Box sx={{ 
                                                        bgcolor: '#E8F5E9', 
                                                        color: '#2E7D32', 
                                                        px: 1, 
                                                        py: 0.5, 
                                                        borderRadius: 1,
                                                        fontWeight: 700,
                                                        fontSize: '0.85rem',
                                                        minWidth: 45,
                                                        textAlign: 'center',
                                                    }}>
                                                        {option.code}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {option.city}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {option.name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                label="To"
                                                placeholder="City or airport code"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <FlightLand sx={{ mr: 1, color: '#999', fontSize: 20 }} />
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            />
                                        )}
                                    />

                                    {/* Dates row */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Depart"
                                            type="date"
                                            value={searchDepartDate}
                                            onChange={(e) => setSearchDepartDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: today }}
                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                        {tripType === 'roundtrip' && (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Return"
                                                type="date"
                                                value={searchReturnDate}
                                                onChange={(e) => setSearchReturnDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ min: searchDepartDate || today }}
                                                sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            />
                                        )}
                                    </Box>

                                    {/* Passengers with stepper */}
                                    <StepperInput
                                        value={searchPassengers}
                                        onChange={setSearchPassengers}
                                        min={1}
                                        max={9}
                                        label="Passengers"
                                        icon={<Person sx={{ color: '#999', fontSize: 20 }} />}
                                    />

                                    {/* Class selector */}
                                    <TextField
                                        fullWidth
                                        size="small"
                                        select
                                        label="Class"
                                        value={cabinClass}
                                        onChange={(e) => setCabinClass(e.target.value as any)}
                                        SelectProps={{ native: true }}
                                        sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    >
                                        <option value="economy">Economy</option>
                                        <option value="premium">Premium Economy</option>
                                        <option value="business">Business</option>
                                        <option value="first">First</option>
                                    </TextField>

                                    {/* Search button - full width on mobile */}
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleSearch}
                                        disabled={searching}
                                        startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <Search />}
                                        sx={{
                                            height: 48,
                                            bgcolor: '#2196F3',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            '&:hover': { bgcolor: '#1976D2' },
                                        }}
                                    >
                                        {searching ? 'Searching...' : 'Search Flights'}
                                    </Button>
                                </Box>
                            ) : (
                                /* DESKTOP LAYOUT */
                                <Grid container spacing={2}>
                                    {/* Origin & Destination */}
                                    <Grid item xs={12} sm={5}>
                                        <Autocomplete
                                            freeSolo
                                            options={filterAirports(searchOrigin)}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                            inputValue={searchOrigin}
                                            onInputChange={(_, newValue) => setSearchOrigin(newValue)}
                                            onChange={(_, newValue) => {
                                                if (newValue && typeof newValue !== 'string') {
                                                    setSearchOrigin(newValue.code + ' - ' + newValue.city);
                                                }
                                            }}
                                            PopperComponent={AutocompletePopper}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props} sx={{ py: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                        <Box sx={{ 
                                                            bgcolor: '#E3F2FD', 
                                                            color: '#1976D2', 
                                                            px: 1, 
                                                            py: 0.5, 
                                                            borderRadius: 1,
                                                            fontWeight: 700,
                                                            fontSize: '0.85rem',
                                                            minWidth: 45,
                                                            textAlign: 'center',
                                                        }}>
                                                            {option.code}
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {option.city}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.name}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    label="From"
                                                    placeholder="City or airport code"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <FlightTakeoff sx={{ mr: 1, color: '#999' }} />
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <IconButton onClick={handleSwapCities} sx={{ bgcolor: 'white', border: '1px solid #ddd' }}>
                                            <SwapHoriz />
                                        </IconButton>
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <Autocomplete
                                            freeSolo
                                            options={filterAirports(searchDestination)}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                            inputValue={searchDestination}
                                            onInputChange={(_, newValue) => setSearchDestination(newValue)}
                                            onChange={(_, newValue) => {
                                                if (newValue && typeof newValue !== 'string') {
                                                    setSearchDestination(newValue.code + ' - ' + newValue.city);
                                                }
                                            }}
                                            PopperComponent={AutocompletePopper}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props} sx={{ py: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                        <Box sx={{ 
                                                            bgcolor: '#E8F5E9', 
                                                            color: '#2E7D32', 
                                                            px: 1, 
                                                            py: 0.5, 
                                                            borderRadius: 1,
                                                            fontWeight: 700,
                                                            fontSize: '0.85rem',
                                                            minWidth: 45,
                                                            textAlign: 'center',
                                                        }}>
                                                            {option.code}
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {option.city}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.name}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    label="To"
                                                    placeholder="City or airport code"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <FlightLand sx={{ mr: 1, color: '#999' }} />
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Dates */}
                                    <Grid item xs={6} sm={3}>
                                        <TextField
                                            fullWidth
                                            label="Depart"
                                            type="date"
                                            value={searchDepartDate}
                                            onChange={(e) => setSearchDepartDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: today }}
                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    {tripType === 'roundtrip' && (
                                        <Grid item xs={6} sm={3}>
                                            <TextField
                                                fullWidth
                                                label="Return"
                                                type="date"
                                                value={searchReturnDate}
                                                onChange={(e) => setSearchReturnDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                inputProps={{ min: searchDepartDate || today }}
                                                sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            />
                                        </Grid>
                                    )}

                                    {/* Passengers & Class */}
                                    <Grid item xs={6} sm={tripType === 'roundtrip' ? 2 : 3}>
                                        <TextField
                                            fullWidth
                                            label="Passengers"
                                            type="number"
                                            value={searchPassengers}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value) || 1;
                                                setSearchPassengers(Math.min(9, Math.max(1, val)));
                                            }}
                                            inputProps={{ min: 1, max: 9 }}
                                            InputProps={{
                                                startAdornment: <Person sx={{ mr: 1, color: '#999' }} />,
                                            }}
                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={tripType === 'roundtrip' ? 2 : 3}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Class"
                                            value={cabinClass}
                                            onChange={(e) => setCabinClass(e.target.value as any)}
                                            SelectProps={{ native: true }}
                                            sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        >
                                            <option value="economy">Economy</option>
                                            <option value="premium">Premium Economy</option>
                                            <option value="business">Business</option>
                                            <option value="first">First</option>
                                        </TextField>
                                    </Grid>

                                    {/* Search Button */}
                                    <Grid item xs={12} sm={2}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={handleSearch}
                                            disabled={searching}
                                            startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <Search />}
                                            sx={{
                                                height: '56px',
                                                bgcolor: '#2196F3',
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                '&:hover': { bgcolor: '#1976D2' },
                                            }}
                                        >
                                            {searching ? '...' : 'Search'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                    </Collapse>
                </Box>

                {/* Error alert */}
                {error && (
                    <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>{error}</Alert>
                )}

                {/* ============================================================ */}
                {/* RESULTS SECTION */}
                {/* ============================================================ */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {hasSearched && (
                        <>
                            {/* Results header */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                mb: 2, 
                                flexWrap: 'wrap', 
                                gap: 1 
                            }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {filteredResults.length} flight{filteredResults.length !== 1 ? 's' : ''} found
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={`${selectedFlights.length}/${maxSelections}`}
                                        size="small"
                                        color={selectedFlights.length > 0 ? 'primary' : 'default'}
                                        sx={{ 
                                            bgcolor: selectedFlights.length > 0 ? '#E3F2FD' : undefined, 
                                            color: selectedFlights.length > 0 ? '#2196F3' : undefined 
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        startIcon={showFilters ? <ExpandLess /> : <FilterList />}
                                        onClick={() => setShowFilters(!showFilters)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Filters
                                    </Button>
                                </Box>
                            </Box>

                            {/* Filters */}
                            <Collapse in={showFilters}>
                                <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#FAFAFA' }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="caption" color="text.secondary">
                                                Price range
                                            </Typography>
                                            <Slider
                                                value={priceRange}
                                                onChange={(_, value) => setPriceRange(value as number[])}
                                                valueLabelDisplay="auto"
                                                valueLabelFormat={(v) => `$${v}`}
                                                min={0}
                                                max={1000}
                                                sx={{ color: '#2196F3' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography variant="caption" color="text.secondary">
                                                Max stops
                                            </Typography>
                                            <Slider
                                                value={maxStops}
                                                onChange={(_, value) => setMaxStops(value as number)}
                                                valueLabelDisplay="auto"
                                                min={0}
                                                max={3}
                                                marks={[
                                                    { value: 0, label: 'Direct' },
                                                    { value: 1, label: '1' },
                                                    { value: 2, label: '2' },
                                                    { value: 3, label: '3+' },
                                                ]}
                                                sx={{ color: '#2196F3' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={directOnly}
                                                        onChange={(e) => setDirectOnly(e.target.checked)}
                                                        sx={{ '&.Mui-checked': { color: '#2196F3' } }}
                                                    />
                                                }
                                                label="Direct flights only"
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Collapse>

                            {/* Flight cards */}
                            {filteredResults.map((flight) => (
                                <Card
                                    key={flight.id}
                                    sx={{
                                        mb: 1.5,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        border: selectedFlights.includes(flight.id) ? '2px solid #2196F3' : '1px solid #e0e0e0',
                                        bgcolor: selectedFlights.includes(flight.id) ? '#E3F2FD' : 'white',
                                        transition: 'all 0.2s',
                                        '&:hover': { borderColor: '#2196F3' },
                                    }}
                                    onClick={() => toggleFlightSelection(flight.id)}
                                >
                                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                                        {/* Mobile-optimized card layout */}
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <Checkbox
                                                checked={selectedFlights.includes(flight.id)}
                                                sx={{ 
                                                    p: 0.5,
                                                    '&.Mui-checked': { color: '#2196F3' } 
                                                }}
                                            />

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                {/* Airline info */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Typography variant="body1">{flight.airlineLogo}</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {flight.airline}
                                                    </Typography>
                                                    {flight.recommended && (
                                                        <Chip
                                                            icon={<Recommend sx={{ fontSize: 12 }} />}
                                                            label="Best"
                                                            size="small"
                                                            sx={{ 
                                                                bgcolor: '#E8F5E9', 
                                                                color: '#2E7D32', 
                                                                fontSize: '0.65rem', 
                                                                height: 18,
                                                                '& .MuiChip-icon': { fontSize: 12 }
                                                            }}
                                                        />
                                                    )}
                                                </Box>

                                                {/* Flight times */}
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: { xs: 1, sm: 2 },
                                                    mb: 0.5,
                                                }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                            {flight.departureTime}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {flight.departureAirport}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ flex: 1, textAlign: 'center', px: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {flight.duration}
                                                        </Typography>
                                                        <Divider sx={{ my: 0.5 }}>
                                                            <Chip 
                                                                label={flight.stops === 0 ? 'Direct' : `${flight.stops} stop`} 
                                                                size="small" 
                                                                sx={{ 
                                                                    fontSize: '0.6rem', 
                                                                    height: 16,
                                                                    bgcolor: flight.stops === 0 ? '#E8F5E9' : '#FFF3E0',
                                                                    color: flight.stops === 0 ? '#2E7D32' : '#E65100',
                                                                }} 
                                                            />
                                                        </Divider>
                                                    </Box>
                                                    
                                                    <Box sx={{ textAlign: 'right' }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                            {flight.arrivalTime}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {flight.arrivalAirport}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Return flight (if roundtrip) */}
                                                {flight.returnFlight && (
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: { xs: 1, sm: 2 },
                                                        pt: 1,
                                                        mt: 1,
                                                        borderTop: '1px dashed #ddd',
                                                    }}>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {flight.returnFlight.departureTime}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {flight.arrivalAirport}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {flight.returnFlight.duration}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {flight.returnFlight.arrivalTime}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {flight.departureAirport}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Price */}
                                            <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF6B35', lineHeight: 1 }}>
                                                    ${flight.price}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {tripType === 'roundtrip' ? 'round trip' : 'one way'}
                                                </Typography>
                                                {flight.seatsLeft && flight.seatsLeft < 10 && (
                                                    <Typography variant="caption" sx={{ color: '#F44336', display: 'block' }}>
                                                        {flight.seatsLeft} left
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}

                            {filteredResults.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Flight sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                    <Typography color="text.secondary">
                                        No flights match your filters.
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}

                    {/* Empty state */}
                    {!hasSearched && !searching && (
                        <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                            <Flight sx={{ fontSize: { xs: 48, sm: 64 }, color: '#BBDEFB', mb: 1 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                Search for flights
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Enter your travel details above
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* ============================================================ */}
            {/* FOOTER ACTIONS */}
            {/* ============================================================ */}
            <DialogActions sx={{ 
                p: 2, 
                borderTop: '1px solid #eee',
                gap: 1,
            }}>
                <Button 
                    onClick={onClose} 
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSaveSelections}
                    disabled={saving || selectedFlights.length === 0}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                    sx={{
                        bgcolor: '#2196F3',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { bgcolor: '#1976D2' },
                    }}
                >
                    {saving ? 'Saving...' : `Add ${selectedFlights.length} Flight${selectedFlights.length !== 1 ? 's' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FlightSearchDialog;
